import React, { useState, useCallback, useMemo } from "react";
import { makeStyles } from "@civicplus/preamble-ui/lib/Utilities/ThemeHelper";
import Button from "@civicplus/preamble-ui/lib/Button";
import GetAppIcon from "@material-ui/icons/GetApp";
import Loader from "@civicplus/preamble-ui/lib/Loader";
import { v1Api, remoteAxios } from "../../../../apis";
import { RemoteFile } from "../interfaces";
import { useSnackbar } from "notistack";
import FileDownload from "js-file-download";
import classNames from "classnames";

interface IDownloadFileButton {
  remoteFile: RemoteFile;
  btnSize?: "small" | "medium" | "large";
  loaderSize?: number;
  hideBtn?: boolean;
  cpMediaOnly?: boolean;
  className?: string;
}

const useStyles = makeStyles(theme => ({
  icon: {
    display: "flex",
    color: theme.palette.primary.main,
  },
}));

const DownloadFileButton = React.forwardRef<
  HTMLDivElement,
  IDownloadFileButton
>((props, ref) => {
  const {
    remoteFile,
    btnSize = "small",
    loaderSize = 42,
    hideBtn,
    cpMediaOnly,
    className,
  } = props;
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();
  const [progress, setProgress] = useState(-1);
  const [retryToGeneratePlainTextFile, setRetryToGeneratePlainTextFile] =
    useState(false);

  const fileStreamStatus = useMemo(() => {
    const status = {
      completed: "Completed",
      running: "Running",
      failed: "Failed",
      started: "Started",
    };

    return status;
  }, []);

  const remoteFileType = useMemo(() => {
    const types = {
      agendaItem: 1,
      attachment: 3,
    };

    return types;
  }, []);

  const getPlainTextFileStatusUrl = useCallback(() => {
    let basePath = "Meetings/GetPlainTextFileStatus";
    if (
      cpMediaOnly &&
      (remoteFile.fileType === 1 || remoteFile.fileType === 4)
    ) {
      basePath = "Events/GetEventPlainTextFileStatus";
    }
    let params = "";
    if (cpMediaOnly) {
      params = retryToGeneratePlainTextFile
        ? `(fileId=${remoteFile.fileId},fileType=${remoteFile.fileType},retry=true)`
        : `(fileId=${remoteFile.fileId},fileType=${remoteFile.fileType},retry=false)`;
    } else {
      params = retryToGeneratePlainTextFile
        ? `(fileId=${remoteFile.fileId},retry=true)`
        : `(fileId=${remoteFile.fileId},retry=false)`;
    }

    if (retryToGeneratePlainTextFile) {
      setRetryToGeneratePlainTextFile(false);
    }
    return basePath + params;
  }, [
    remoteFile,
    retryToGeneratePlainTextFile,
    setRetryToGeneratePlainTextFile,
    cpMediaOnly,
  ]);

  const handleAction = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();

      function displayTxtFileSnackBarMessage(
        status?: string,
        fileName?: string
      ) {
        if (!fileName) {
          if (status === fileStreamStatus.started) {
            enqueueSnackbar(
              "We have started preparing your Plain Text file for download. The file will be published soon.  Please try your download again in a few moments.",
              {
                variant: "success",
              }
            );
          } else if (status === fileStreamStatus.running) {
            enqueueSnackbar(
              "We are continuing to prepare your Plain Text file for download.  The file will be published soon.  Please try your download again in a few moments.",
              {
                variant: "info",
              }
            );
          }
        } else {
          enqueueSnackbar(
            `There was an error downloading the document ${fileName}.txt. Please try again.`,
            {
              variant: "error",
            }
          );
        }
      }

      function getPlainTextFileStatus() {
        const url = getPlainTextFileStatusUrl();

        v1Api
          .get(url, {
            responseType: "json",
          })
          .then(response => {
            if (response.data) {
              if (response.data.status === fileStreamStatus.completed) {
                setProgress(0);
                downloadFileStream();
              } else if (response.data.status === fileStreamStatus.started) {
                displayTxtFileSnackBarMessage(fileStreamStatus.started);
              } else if (response.data.status === fileStreamStatus.running) {
                displayTxtFileSnackBarMessage(fileStreamStatus.running);
              } else {
                displayTxtFileSnackBarMessage(
                  fileStreamStatus.failed,
                  remoteFile.name
                );
                setRetryToGeneratePlainTextFile(true);
              }
            }

            return true;
          })
          .catch(error => {
            enqueueSnackbar(
              `There was an error while getting the status of the document "${remoteFile.name}.txt ${error.message}`,
              {
                variant: "error",
              }
            );
            return false;
          });
      }

      function downloadFileStream() {
        const downloadFileApiUrl = cpMediaOnly
          ? `Events/GetEventFileStream(fileId=${remoteFile.fileId},fileType=${
              remoteFile.fileType
            },plainText=${
              remoteFile.plainText === undefined ? false : remoteFile.plainText
            })`
          : `Meetings/GetMeetingFileStream(fileId=${
              remoteFile.fileId
            },plainText=${
              remoteFile.plainText === undefined ? false : remoteFile.plainText
            })`;
        const urlDownloadFile = remoteFile.streamUrl || downloadFileApiUrl;

        const axiosInstance = remoteFile.streamUrl ? remoteAxios : v1Api;

        axiosInstance
          .get(urlDownloadFile, {
            responseType: "blob",
            onDownloadProgress: progressEvent => {
              if (progressEvent.total) {
                const percentCompleted = Math.round(
                  (progressEvent.loaded * 100) / progressEvent.total
                );
                setProgress(percentCompleted);
              }
            },
          })
          .then(response => {
            if (response.data && response.data.size > 0) {
              FileDownload(
                remoteFile.streamUrl
                  ? new Blob([response.data])
                  : response.data,
                `${remoteFile.name}.${
                  remoteFile.plainText === undefined ||
                  remoteFile.plainText === false
                    ? "pdf"
                    : "txt"
                }`
              );
            } else {
              enqueueSnackbar(
                `There was an error downloading the meeting document "${
                  remoteFile.name
                }.${
                  remoteFile.plainText === undefined ||
                  remoteFile.plainText === false
                    ? "pdf"
                    : "txt"
                }"`,
                {
                  variant: "error",
                  action: (
                    <Button
                      color="inherit"
                      variant="outlined"
                      size="small"
                      onClick={handleAction}
                    >
                      Retry
                    </Button>
                  ),
                }
              );
            }
            return true;
          })
          .finally(() => setProgress(-1))
          .catch(error => {
            enqueueSnackbar(
              `There was an error downloading the document "${
                remoteFile.name
              }.${
                remoteFile.plainText === undefined ||
                remoteFile.plainText === false
                  ? "pdf"
                  : "txt"
              }": ${error.message}`,
              {
                variant: "error",
                action: (
                  <Button
                    color="inherit"
                    variant="outlined"
                    size="small"
                    onClick={handleAction}
                  >
                    Retry
                  </Button>
                ),
              }
            );
            return false;
          });
      }

      function downloadAttachmentFile() {
        const downloadFileApiUrl = `Meetings/GetAttachmentFile(fileId=${remoteFile.fileId})`;
        const urlDownloadFile = remoteFile.streamUrl || downloadFileApiUrl;

        const axiosInstance = remoteFile.streamUrl ? remoteAxios : v1Api;

        axiosInstance
          .get(urlDownloadFile, {
            responseType: "blob",
            onDownloadProgress: progressEvent => {
              if (progressEvent.total) {
                const percentCompleted = Math.round(
                  (progressEvent.loaded * 100) / progressEvent.total
                );
                setProgress(percentCompleted);
              }
            },
          })
          .then(response => {
            if (response.data) {
              FileDownload(response.data, `${remoteFile.name}.pdf`);
            }
            return true;
          })
          .finally(() => setProgress(-1))
          .catch(error => {
            enqueueSnackbar(
              `There was an error downloading the document "${
                remoteFile.name
              }.${
                remoteFile.plainText === undefined ||
                remoteFile.plainText === false
                  ? "pdf"
                  : "txt"
              }": ${error.message}`,
              {
                variant: "error",
                action: (
                  <Button
                    color="inherit"
                    variant="outlined"
                    size="small"
                    onClick={handleAction}
                  >
                    Retry
                  </Button>
                ),
              }
            );
            return false;
          });
      }

      if (remoteFile.fileType !== remoteFileType.attachment) {
        if (
          remoteFile.plainText !== undefined &&
          remoteFile.plainText !== false
        ) {
          getPlainTextFileStatus();
        } else {
          setProgress(0);

          downloadFileStream();
        }
      } else {
        setProgress(0);

        downloadAttachmentFile();
      }
    },
    [
      remoteFile,
      remoteFileType,
      enqueueSnackbar,
      getPlainTextFileStatusUrl,
      fileStreamStatus,
      cpMediaOnly,
    ]
  );

  if (progress >= 0 && progress < 100) {
    return (
      <Loader
        aria-label="Downloading File"
        value={progress}
        size={loaderSize}
      />
    );
  } else {
    if (hideBtn) {
      return (
        // This is only rendered when this item is used as an icon in the menu, and there is a regular menu item that is accessible
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div ref={ref} className={classes.icon} onClick={handleAction}>
          <GetAppIcon color="inherit" />
        </div>
      );
    } else {
      return (
        <Button
          type="icon"
          title="Download"
          stopPropagation={true}
          onClick={handleAction}
          size={btnSize}
          className={classNames(className, classes.icon)}
        >
          <GetAppIcon color="inherit" />
        </Button>
      );
    }
  }
});

DownloadFileButton.displayName = "DownloadFileButton";

export default DownloadFileButton;
